<!--
  PurchaseButton - Handles Stripe checkout for various product types

  For bulk purchases, opens a quantity dialog then redirects to Stripe Checkout.
  For other purchases (membership, vip, parking, children), redirects directly to Stripe Checkout.
-->
<template>
  <div>
    <Button
      :icon="icon || 'pi pi-ticket'"
      class="purchase-button"
      :class="[
        props.fullWidth ? 'w-full' : '',
        props.buttonClass,
      ]"
      :size="props.buttonSize === 'small' ? 'small' : undefined"
      :label="props.buttonIconOnly ? undefined : buttonLabel"
      :aria-label="buttonLabel"
      :title="props.buttonIconOnly ? buttonLabel : undefined"
      :disabled="isDisabled"
      :severity="isDisabled ? 'secondary' : props.buttonSeverity"
      :loading="isLoading"
      @click="handleClick"
    />

    <!-- Checkout error message (for non-bulk) -->
    <p
      v-if="checkoutError && props.ticketType !== TICKET_TYPES.BULK"
      class="kb-field-error mt-1.5 mb-0"
      role="alert"
    >
      {{ checkoutError }}
    </p>

    <!-- Bulk Purchase Quantity Dialog -->
    <Dialog
      v-if="props.ticketType === TICKET_TYPES.BULK"
      v-model:visible="quantityDialogVisible"
      modal
      header="Bulk membership purchase"
      :style="{ width: '26rem' }"
      :breakpoints="{ '575px': 'calc(100vw - 2rem)' }"
      :draggable="false"
    >
      <div class="kb-form">
        <p class="kb-dialog-lead">
          Enter the number of memberships to purchase. Each membership will generate a unique redemption code.
        </p>

        <p
          v-if="checkoutError"
          class="kb-callout-alert m-0"
          role="alert"
        >
          {{ checkoutError }}
        </p>

        <KbField
          field-id="bulk-quantity"
          label="Quantity"
        >
          <InputNumber
            v-model="bulkQuantity"
            input-id="bulk-quantity"
            :min="1"
            :max="100"
            show-buttons
            button-layout="horizontal"
            :step="1"
            size="small"
            :input-style="{ width: '4rem' }"
            increment-button-icon="pi pi-plus"
            decrement-button-icon="pi pi-minus"
          />
        </KbField>
      </div>

      <template #footer>
        <Button
          label="Cancel"
          text
          severity="secondary"
          size="small"
          @click="quantityDialogVisible = false"
        />
        <Button
          label="Continue to checkout"
          icon="pi pi-shopping-cart"
          size="small"
          :loading="isLoading"
          @click="startBulkCheckout"
        />
      </template>
    </Dialog>

    <!-- Parking Terms Dialog -->
    <Dialog
      v-if="props.ticketType === TICKET_TYPES.PARKING"
      v-model:visible="parkingDialogVisible"
      modal
      header="Contraption parking pass"
      :style="{ width: '28rem' }"
      :breakpoints="{ '575px': 'calc(100vw - 2rem)' }"
      :draggable="false"
    >
      <div class="kb-form">
        <p
          v-if="checkoutError"
          class="kb-callout-alert m-0"
          role="alert"
        >
          {{ checkoutError }}
        </p>

        <KbField
          field-id="parking-quantity"
          label="Quantity"
        >
          <InputNumber
            v-model="parkingQuantity"
            input-id="parking-quantity"
            :min="1"
            :max="10"
            show-buttons
            button-layout="horizontal"
            size="small"
            :input-style="{ width: '4rem' }"
            increment-button-icon="pi pi-plus"
            decrement-button-icon="pi pi-minus"
          />
        </KbField>

        <!-- The parking product's terms, acknowledged with a checkbox -->
        <template v-if="parkingTermsMessage">
          <p class="kb-form-note kb-form-note-warn whitespace-pre-line">
            {{ parkingTermsMessage }}
          </p>
          <div class="kb-check-row">
            <Checkbox
              v-model="parkingTermsAccepted"
              input-id="parking-terms-checkbox"
              :binary="true"
            />
            <label for="parking-terms-checkbox">
              I understand and accept these terms
            </label>
          </div>
        </template>
      </div>

      <template #footer>
        <Button
          label="Cancel"
          text
          severity="secondary"
          size="small"
          @click="parkingDialogVisible = false"
        />
        <Button
          label="Continue to checkout"
          icon="pi pi-shopping-cart"
          size="small"
          :loading="isLoading"
          :disabled="Boolean(parkingTermsMessage) && !parkingTermsAccepted"
          @click="startParkingCheckout"
        />
      </template>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import { useEventListener } from '@vueuse/core'
import type { PropType } from 'vue'
import { computed, ref } from 'vue'
import { extractFetchErrorMessage } from '~/utils/errorMessages'

// Ticket types
const TICKET_TYPES = {
  MEMBERSHIP: 'membership',
  VIP: 'vip',
  PARKING: 'parking',
  CHILDREN: 'children',
  BULK: 'bulk',
} as const

type TicketType = typeof TICKET_TYPES[keyof typeof TICKET_TYPES]

const props = defineProps({
  ticketType: {
    type: String as PropType<TicketType>,
    required: true,
  },
  buttonLabel: {
    type: String,
    default: '',
  },
  buttonSeverity: {
    type: String,
    default: 'primary',
  },
  fullWidth: {
    type: Boolean,
    default: false,
  },
  icon: {
    type: String,
    default: 'pi pi-dollar',
  },
  buttonSize: {
    type: String,
    default: 'normal',
  },
  buttonIconOnly: {
    type: Boolean,
    default: false,
  },
  buttonClass: {
    type: String,
    default: '',
  },
  childId: {
    type: String,
    default: '',
  },
  childrenIds: {
    type: Array as PropType<string[]>,
    default: () => [],
  },
  subkreweId: {
    type: String,
    default: '',
  },
  closeOnSuccess: {
    type: Boolean,
    default: false,
  },
  // No longer does anything (the button always fits its label); declared so
  // the callers that still pass it don't leak it onto the wrapper as an attribute
  noMinWidth: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['order-complete', 'error', 'checkout-opened', 'checkout-closed'])

const route = useRoute()

const defaultButtonLabels: Record<string, string> = {
  [TICKET_TYPES.MEMBERSHIP]: 'Pay dues',
  [TICKET_TYPES.VIP]: 'Chewbacchanal VIP upgrade',
  [TICKET_TYPES.PARKING]: 'Buy contraption parking',
  [TICKET_TYPES.CHILDREN]: 'Pay children membership dues',
  [TICKET_TYPES.BULK]: 'Buy multiple memberships',
}

const buttonLabel = computed(() => {
  return props.buttonLabel || defaultButtonLabels[props.ticketType] || 'Purchase ticket'
})

// Only bulk purchases are enabled; others are disabled pending full migration
const isDisabled = computed(() => {
  return false // All ticket types now enabled
})

// Loading state
const isLoading = ref(false)
const checkoutError = ref<string | null>(null)

// Back from Stripe with the browser's Back button can restore this page as
// it was left: still busy
useEventListener(window, 'pageshow', (event: PageTransitionEvent) => {
  if (event.persisted) isLoading.value = false
})

// Bulk purchase dialog state
const quantityDialogVisible = ref(false)
const bulkQuantity = ref(5)

// Parking purchase dialog state
const parkingDialogVisible = ref(false)
const parkingQuantity = ref(1)
const parkingTermsAccepted = ref(false)
const parkingTermsMessage = ref<string | null>(null)

// Fetch parking terms when needed
const productsStore = useProductsStore()
const { paradeYear, ensureLoaded } = useParadeYear()

async function fetchParkingTerms() {
  await ensureLoaded()
  const year = String(paradeYear.value)
  await productsStore.fetchProductsForYear(year)
  const parkingProduct = productsStore.productsArray.find(p => p.type === 'parking' && p.year === year)
  if (parkingProduct?.termsMessage) {
    parkingTermsMessage.value = parkingProduct.termsMessage
  }
}

async function handleClick() {
  if (props.ticketType === TICKET_TYPES.BULK) {
    quantityDialogVisible.value = true
    emit('checkout-opened')
  }
  else if (props.ticketType === TICKET_TYPES.PARKING) {
    await fetchParkingTerms()
    parkingDialogVisible.value = true
    parkingTermsAccepted.value = false
    emit('checkout-opened')
  }
  else {
    await startCheckout()
  }
}

async function startCheckout() {
  if (isLoading.value) return
  isLoading.value = true
  checkoutError.value = null

  try {
    // Build request body
    const requestBody: Record<string, unknown> = {
      type: props.ticketType,
      quantity: 1,
      returnUrl: route.fullPath,
    }

    // Add childrenIds for children ticket type
    if (props.ticketType === TICKET_TYPES.CHILDREN) {
      const ids = props.childrenIds.length > 0 ? props.childrenIds : props.childId ? [props.childId] : []
      if (ids.length === 0) {
        checkoutError.value = 'No children selected for tickets'
        isLoading.value = false
        return
      }
      requestBody.childrenIds = ids
      requestBody.quantity = ids.length
    }

    const response = await $fetch('/api/stripe/create-checkout', {
      method: 'POST',
      body: requestBody,
    })

    if (response.url) {
      // Stays busy while the browser leaves for Stripe, so a second tap
      // can't open a second checkout
      window.location.href = response.url
      return
    }
    checkoutError.value = 'Failed to create checkout session'
    isLoading.value = false
  }
  catch (err: unknown) {
    const errorMessage = extractFetchErrorMessage(err, 'An error occurred')
    checkoutError.value = errorMessage
    isLoading.value = false
    emit('error', err)
  }
}

async function startParkingCheckout() {
  if (parkingTermsMessage.value && !parkingTermsAccepted.value) {
    return
  }
  if (isLoading.value) return

  isLoading.value = true
  checkoutError.value = null

  try {
    const response = await $fetch('/api/stripe/create-checkout', {
      method: 'POST',
      body: {
        type: TICKET_TYPES.PARKING,
        quantity: parkingQuantity.value,
        returnUrl: route.fullPath,
      },
    })

    if (response.url) {
      parkingDialogVisible.value = false
      window.location.href = response.url
      return
    }
    checkoutError.value = 'Failed to create checkout session'
    isLoading.value = false
  }
  catch (err: unknown) {
    const errorMessage = extractFetchErrorMessage(err, 'An error occurred')
    checkoutError.value = errorMessage
    isLoading.value = false
    emit('error', err)
  }
}

async function startBulkCheckout() {
  if (!props.subkreweId) {
    checkoutError.value = 'SubKrewe ID is required for bulk purchases'
    return
  }

  if (bulkQuantity.value < 1) {
    checkoutError.value = 'Quantity must be at least 1'
    return
  }
  if (isLoading.value) return

  isLoading.value = true
  checkoutError.value = null

  try {
    const response = await $fetch('/api/stripe/bulk-checkout', {
      method: 'POST',
      body: {
        subkreweId: props.subkreweId,
        quantity: bulkQuantity.value,
        returnUrl: route.fullPath,
      },
    })

    if (response.url) {
      // Close dialog and redirect to Stripe Checkout
      quantityDialogVisible.value = false
      window.location.href = response.url
      return
    }
    checkoutError.value = 'Failed to create checkout session'
    isLoading.value = false
  }
  catch (err: unknown) {
    const errorMessage = extractFetchErrorMessage(err, 'An error occurred')
    checkoutError.value = errorMessage
    isLoading.value = false
    emit('error', err)
  }
}

// Expose open function for programmatic triggering
async function open() {
  if (props.ticketType === TICKET_TYPES.BULK) {
    quantityDialogVisible.value = true
    emit('checkout-opened')
  }
  else {
    await startCheckout()
  }
}

defineExpose({ open })
</script>

<style scoped>
/* Block-level, so the wrapper is exactly as tall as the button (an inline
   button sits on a text line and gains descender space below it); a
   button's auto width still fits its label */
.purchase-button {
  display: flex;
}
</style>
